import React from 'react';
import { loadFromLocalStorage } from '../../../Common/Validation/LocalStorage';
import { TableColumn } from 'react-data-table-component';
import DataGrid from '../../../Component/DataGrid/DataGrid';
import NetworkServices from '../../../Services/NetworkService';
import { ShowAlert } from '../../../Component/ShowAlert';
import { INetwork, NetworksState, NetworksProps } from './InterfaceNetwork'
import { SyncContext, SyncContextType } from '../../../Contexts/BatchSyncContext';
import UserAndPermissionService from '../../../Services/UserAndPermissionService';
import { Switch } from '@mui/material';
import { Texts } from '../../../Component/Reusables/Utility';
import moment from 'moment-timezone';
import ReusableDrawer from '../../../Component/RightDrawer/ReusableDrawer';
import { fetchRecipientCountryList } from '../../../Component/Reusables/Recipient';
import { ISelectedDateRangeString } from '../../../Component/DataGrid/Interface';

enum Fields {
    maintenanceStartDate = 'maintenanceStartDate',
    maintenanceEndDate = 'maintenanceEndDate'
}

// Main class for Networks component
class Networks extends React.Component<NetworksProps, NetworksState> {
    static contextType = SyncContext;
    context!: SyncContextType;

    // Table columns for DataGrid
    private tableColumns: TableColumn<INetwork>[] = [
        {
            name: 'Network Name',
            selector: row => row.networkName,
            sortable: true,
            id: "networkName",
            grow: 1
        },
        {
            name: 'Display Name',
            selector: row => row.displayName,
            sortable: true,
            id: "displayName",
            grow: 2
        },
        {
            name: 'Country Name',
            selector: row => row.countryName,
            sortable: true,
            id: "countryName",
            grow: 1
        },
        {
            name: 'Network Type',
            selector: row => row.networkType,
            sortable: true,
            id: "networkType",
            grow: 1
        },
        {
            name: 'Time Zone',
            selector: row => `${row.timeZone} ${row.timeZoneOffset}`,
            sortable: true,
            id: "timeZone",
            grow: 2
        },
        {
            name: 'Under Maintenance',
            id: 'maintenance',
            grow: 1,
            sortable: true,
            cell: (row) => (
                <div >
                    <Switch
                        checked={row.isUnderMaintenance}
                        onChange={(e) => this.handleMaintenanceToggle(row, e.target.checked)}
                        color="secondary"
                    />
                </div>
            )
        },
        {
            name: "Maintenance Start (Network’s Local Date/Time)",
            id: Fields.maintenanceStartDate,
            grow: 2,
            minWidth: '210px',
            cell: (row) => (
                <input
                    type="datetime-local"
                    value={moment(row.maintenanceStartDate).format('YYYY-MM-DDTHH:mm') || ''}
                    onChange={(e) => this.handleDateChange(row.networkId, Fields.maintenanceStartDate, e.target.value)}
                    disabled={!row.isUnderMaintenance}
                    style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        height: '40px',
                        fontFamily: 'inherit'
                    }}
                />
            )
        },
        {
            name: "Maintenance End (Network’s Local Date/Time)",
            id: Fields.maintenanceEndDate,
            grow: 2,
            minWidth: '210px',
            cell: (row) => (
                <input
                    type="datetime-local"
                    value={moment(row.maintenanceEndDate).format('YYYY-MM-DDTHH:mm') || ''}
                    onChange={(e) => this.handleDateChange(row.networkId, Fields.maintenanceEndDate, e.target.value)}
                    disabled={!row.isUnderMaintenance}
                    style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        height: '40px',
                        fontFamily: 'inherit'
                    }}
                />
            )
        },

    ];

    constructor(props_: NetworksProps) {
        super(props_);
        this.state = {
            accessToken: "",
            networksList: [],
            currentRowsPerPage: 10,
            currentPage: 1,
            totalRows: 0,
            searchText: "",
            network: {
                isVisible: true,
                isDownloadVisible: true,
            },
            paginationReset: false,
            isDrawerOpen: false,
            selectedCountry: "",
            appliedFiltersCount: 0,
        }
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.network?.isVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const network = permissionResponse.permissions.network

                        this.setState({ network }, () => {
                            this.getAllNetworksList()
                        });

                    } else {
                        this.getAllNetworksList()
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }

    handleDateChange = (networkId: string, fieldName: Fields.maintenanceStartDate | Fields.maintenanceEndDate, newValue: string) => {
        const updatedList = this.state.networksList.map(network => {
            if (network.networkId === networkId) {
                const otherField = fieldName === Fields.maintenanceStartDate ? Fields.maintenanceEndDate : Fields.maintenanceStartDate;

                const newDate = new Date(newValue);
                const otherDate = network[otherField] ? new Date(network[otherField]!) : null;


                if (fieldName === Fields.maintenanceEndDate && otherDate && newDate < otherDate) {
                    ShowAlert("Info", "End date cannot be before start date.", "Info",);
                    return network;
                }

                if (fieldName === Fields.maintenanceStartDate && otherDate && newDate > otherDate) {
                    ShowAlert("Info", "Start date cannot be after end date.", "Info");
                    return network;
                }

                return {
                    ...network,
                    [fieldName]: newValue,
                    isModified: true
                };
            }
            return network;
        });

        this.setState({ networksList: updatedList });
    };

    private handleMaintenanceToggle = (row: INetwork, newValue: boolean) => {
        const updatedList = this.state.networksList.map(network => {
            if (network.networkId === row.networkId) {
                const now = moment.utc().tz(row.timeZone);
                const formattedNow = now.format(Texts.formatDateTimeNetworks);
                const updatedNetwork = {
                    ...network,
                    isUnderMaintenance: newValue,
                    isModified: true
                };

                if (newValue) {
                    updatedNetwork.maintenanceStartDate = formattedNow;
                    if (network.maintenanceEndDate && moment(network.maintenanceEndDate) < now) {
                        updatedNetwork.maintenanceEndDate = '';
                    }
                } else {
                    updatedNetwork.maintenanceEndDate = formattedNow;
                }

                return updatedNetwork;
            }
            return network;
        });

        this.setState({ networksList: updatedList });
    };

    public render() {
        const { network, searchText, totalRows, paginationReset } = this.state;
        return (
            <>
                <div className="body-sub-container table-container">

                    <DataGrid
                        title='Networks'
                        search='network'
                        isSaveNetworkButton={this.handleSaveNetworks}
                        saveNetworkButton={true}
                        defaultSearch={searchText}
                        onSearch={(searchText_: string) => {
                            this.setState({
                                searchText: searchText_,
                                currentPage: 1,
                                appliedFiltersCount: searchText_ ? 0 : this.state.appliedFiltersCount,
                                selectedCountry: this.state.selectedCountry
                            }, () => {
                                this.getAllNetworksList();
                            })
                        }}
                        onApply={() => {
                            this.setState({
                                currentPage: 1
                            }, () => {
                                this.getAllNetworksList();
                            })
                        }}
                        columns={this.tableColumns}
                        data={this.state.networksList}
                        onChangePage={(page: number, totalRows: number) => {
                            this.setState({
                                currentPage: page
                            }, () => {
                                this.getAllNetworksList();
                            })
                        }}
                        onChangeRowsPerPage={(currentRowsPerPage_: number, currentPage_: number) => {
                            this.setState({
                                currentRowsPerPage: currentRowsPerPage_,
                                currentPage: currentPage_
                            }, () => {
                                this.getAllNetworksList();
                            })
                        }}
                        pagination
                        paginationTotalRows={totalRows}
                        exportCsv={network.isDownloadVisible ? true : false}
                        csvFileName={'All Networks'}
                        paginationDefaultPage={1}
                        paginationResetDefaultPage={paginationReset}
                        isDrawerRequired={true}
                        isDrawerOpen={() => this.setState({ isDrawerOpen: true })}
                        appliedFiltersCount={this.state.appliedFiltersCount}
                        selectedCountry={this.state.selectedCountry}
                    />
                    <ReusableDrawer
                        open={this.state.isDrawerOpen}
                        onClose={() => this.setState({ isDrawerOpen: false })}
                        onApply={this.handleDrawerApply}
                        fetchDropdownOptions={this.fetchDropdownOptions}
                        defaultValue={this.state.selectedCountry}
                        onFiltersCountChange={(count) => this.setState({ appliedFiltersCount: count })}
                    />
                </div>
            </>
        )
    }

    fetchDropdownOptions = () => {
        return fetchRecipientCountryList(this.state.accessToken);
    };

    handleDrawerApply = (filters: { selectedId: string }) => {
        const { selectedId } = filters;

        this.setState(
            {
                selectedCountry: selectedId || "",
                currentPage: 1,
            },
            () => {
                this.getAllNetworksList();
            }
        );
    };

    private handleSaveNetworks = async () => {
        try {
            const modifiedNetworks = this.state.networksList.filter(network => network.isModified);
            if (modifiedNetworks.length === 0) {
                ShowAlert("Info", "No changes to save.", "info");
                return;
            }

            const payload = modifiedNetworks.map(network => ({
                networkId: network.networkId,
                isUnderMaintenance: network.isUnderMaintenance ? 1 : 0,
                maintenanceStartDate: this.getUTCDateTimeFromTimezone(network.maintenanceStartDate, network.timeZone),
                maintenanceEndDate: this.getUTCDateTimeFromTimezone(network.maintenanceEndDate, network.timeZone)
            }));

            const response = await NetworkServices.updateNetworkList(this.state.accessToken, payload);

            if (response.ok) {
                ShowAlert("Success", "Networks updated successfully.", "success");
                this.getAllNetworksList();
            } else {
                ShowAlert("Error", "Failed to update networks. Please try again.", "error");
            }
        } catch (error) {
            console.error("Error saving networks:", error);
            ShowAlert("Error", "Unexpected error occurred while saving networks.", "error");
        }
    };

    formatForDateTimeLocal(dateString: string | null | undefined): string {
        if (!dateString) return '';
        return moment(dateString).format(Texts.formatDateTimeNetworks)
    }

    private getLocalDateTimeFromUTCUsingTimezone(dateTime: string | null, timeZone: string) {
        return dateTime ? moment.tz(dateTime, Texts.timeZoneUTC).tz(timeZone).format(Texts.formatDateTimeNetworks) : null;
    }
    private getUTCDateTimeFromTimezone(dateTime: string | null, timeZone: string) {
        return dateTime ? moment.tz(dateTime, timeZone).utc().format(Texts.formatDateTimeNetworks) : null;
    }

    // Fetch all networks
    private async getAllNetworksList() {
        let pageIndex: number = this.state.currentPage;
        let pagination: any = {
            pageNumber: pageIndex,
            recordPerPage: this.state.currentRowsPerPage
        };

        if (this.state.searchText) {
            pagination["searchText"] = this.state.searchText;
        }

        try {
            const response = await NetworkServices.getAllNetworksList(this.state.accessToken, pagination, this.state.selectedCountry);

            if (response.ok) {
                const resp = await response.json();
                let networkLists: INetwork[] = resp.netWorkList;
                networkLists.forEach((network) => {
                    network.maintenanceStartDate = this.getLocalDateTimeFromUTCUsingTimezone(network.maintenanceStartDate, network.timeZone);
                    network.maintenanceEndDate = this.getLocalDateTimeFromUTCUsingTimezone(network.maintenanceEndDate, network.timeZone);
                })
                this.setState({
                    networksList: networkLists,
                    totalRows: resp.totalPages || 0
                });
            } else {
                ShowAlert("Error", "Failed to fetch networks. Please try again later.", "error");
            }
        } catch (error) {
            console.error("Error fetching networks:", error);
            ShowAlert("Error", "An unexpected error occurred while fetching networks.", "error");
        }
    }

}

export default Networks;